import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getSites, deleteSite, checkNow, getLogs } from "../services/api";
import Badge from "./ui/Badge";
import { SiteAnalytics } from "./SiteAnalytics";

export default function MonitorList() {
  const navigate = useNavigate();
  const [sites, setSites] = useState([]);
  const [open, setOpen] = useState(null);   // site id for log panel
  const [performanceOpen, setPerformanceOpen] = useState(null); // site id for performance panel
  const [logs, setLogs] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // site id for delete confirmation
  const [error, setError] = useState(null);

  const loadSites = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await getSites();
      setSites(data);
    } catch (error) {
      console.error("Failed to load sites:", error);
      setError(error.message || "Failed to load sites");
      
      // If unauthorized or data fetch fails, redirect to login
      if (error.message?.includes("401") || error.message?.includes("Unauthorized") || error.message?.includes("failed")) {
        console.error("❌ Data fetch failed - redirecting to login");
        localStorage.setItem("wasLoggedIn", "true");
        localStorage.removeItem("token");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("user");
        
        // Redirect after a short delay to show error
        setTimeout(() => {
          navigate("/login", { replace: true });
        }, 1500);
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);
  
  useEffect(() => { loadSites(); }, [loadSites]);

  function handleDeleteClick(id) {
    setDeleteConfirm(id);
  }

  function handleDeleteCancel() {
    setDeleteConfirm(null);
  }

  async function handleDeleteConfirm(id) {
    try {
      const res = await deleteSite(id);
      if (res.ok) {
        setSites(sites.filter((s) => s.id !== id));
        setDeleteConfirm(null);
      } else {
        alert(res.error || "Delete failed");
      }
    } catch (error) {
      alert("Delete failed: " + error.message);
    }
  }
  
  async function handleCheckNow(id) {
    setBusyId(id);
    try {
      await checkNow(id);
      await loadSites();
      if (open === id) await loadLogs(id);
    } finally {
      setBusyId(null);
    }
  }
  
  async function loadLogs(id) {
    try {
      const { data } = await getLogs(id, 50);
      console.debug("Logs loaded", { siteId: id, count: Array.isArray(data) ? data.length : 0, sample: Array.isArray(data) && data[0] ? data[0] : null });
      setLogs(data);
    } catch (error) {
      console.error("Failed to load logs:", error);
      setLogs([]);
    }
  }
  
  async function toggleLogs(id) {
    if (open === id) {
      setOpen(null);
      setLogs([]);
    } else {
      setOpen(id);
      setPerformanceOpen(null); // Close performance panel
      await loadLogs(id);
    }
  }

  function togglePerformance(id) {
    if (performanceOpen === id) {
      setPerformanceOpen(null);
    } else {
      setPerformanceOpen(id);
      setOpen(null); // Close logs panel
    }
  }

  function formatLastChecked(lastChecked) {
    if (!lastChecked) return "Never";
    const date = new Date(lastChecked);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
    return date.toLocaleDateString();
  }

  // Human-readable response time
  function formatResponseTime(ms) {
    if (ms == null) return "—";
    if (ms < 1000) return `${ms}ms`;
    const seconds = ms / 1000;
    if (seconds < 60) {
      // 1 to 59.9s
      return `${seconds < 10 ? seconds.toFixed(2) : seconds.toFixed(1)}s`;
    }
    const minutes = Math.floor(seconds / 60);
    const remSec = Math.floor(seconds % 60);
    return `${minutes}m ${remSec}s`;
  }


  // Compute quick stats from recent logs
  function computeLogStats(items = []) {
    if (!Array.isArray(items) || items.length === 0) {
      return { count: 0, uptimePct: null, avgMs: null, p95Ms: null, lastCode: null };
    }
    const count = items.length;
    const upCount = items.filter(l => l.status === "UP").length;
    const uptimePct = Math.round((upCount / count) * 1000) / 10; // 1 decimal
    const msVals = items.map(l => l.ms).filter(v => typeof v === "number");
    const avgMs = msVals.length ? Math.round(msVals.reduce((a, b) => a + b, 0) / msVals.length) : null;
    let p95Ms = null;
    if (msVals.length) {
      const sorted = [...msVals].sort((a, b) => a - b);
      const idx = Math.min(sorted.length - 1, Math.floor(0.95 * (sorted.length - 1)));
      p95Ms = sorted[idx];
    }
    const last = items[0];
    const lastCode = last?.code ?? null;
    return { count, uptimePct, avgMs, p95Ms, lastCode };
  }

  function renderSSLStatus(site) {
    if (!site.ssl_check_enabled) {
      return null;
    }

    // Handle different SSL statuses
    if (site.ssl_status === 'NOT_HTTPS') {
      return (
        <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded text-xs">
          SSL: N/A (HTTP)
        </span>
      );
    }

    if (site.ssl_status === 'ERROR') {
      return (
        <span className="px-2 py-1 bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 rounded text-xs">
          SSL: Error
        </span>
      );
    }

    if (site.ssl_status === 'NO_CERTIFICATE') {
      return (
        <span className="px-2 py-1 bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 rounded text-xs">
          SSL: No Certificate
        </span>
      );
    }

    // If SSL check is enabled but no expiration date yet, still checking
    if (!site.ssl_expires_at && (!site.ssl_status || site.ssl_status === 'CHECKING')) {
      return (
        <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded text-xs">
          SSL: Checking...
        </span>
      );
    }

    // Handle valid SSL certificates with expiration dates
    if (site.ssl_expires_at) {
      const expiresAt = new Date(site.ssl_expires_at);
      const now = new Date();
      const diffDays = Math.ceil((expiresAt - now) / (1000 * 60 * 60 * 24));
      
      // Format expiration date as MM/DD/YYYY
      const expirationDate = expiresAt.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
      });

      if (diffDays < 0) {
        return (
          <span className="px-2 py-1 bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 rounded text-xs">
            SSL: Expired {expirationDate}
          </span>
        );
      } else if (diffDays <= 7) {
        return (
          <span className="px-2 py-1 bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 rounded text-xs">
            SSL: {expirationDate} ({diffDays}d left)
          </span>
        );
      } else if (diffDays <= 30) {
        return (
          <span className="px-2 py-1 bg-yellow-100 dark:bg-yellow-900 text-yellow-700 dark:text-yellow-300 rounded text-xs">
            SSL: {expirationDate} ({diffDays}d left)
          </span>
        );
      } else {
        return (
          <span className="px-2 py-1 bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 rounded text-xs">
            SSL Expiration: {expirationDate}
          </span>
        );
      }
    }

    // Fallback for unknown SSL status
    return (
      <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded text-xs">
        SSL: Unknown
      </span>
    );
  }

  function renderRedirectStatus(site) {
    if (!site.redirect_enabled) {
      return null;
    }

    // Redirect loop detected (critical)
    if (site.has_redirect_loop) {
      return (
        <Badge variant="danger" className="text-xs">
          🔄 Redirect Loop
        </Badge>
      );
    }

    // Excessive redirects (warning)
    if (site.redirect_count > 5) {
      return (
        <Badge variant="warning" className="text-xs">
          ⚠️ {site.redirect_count} Redirects
        </Badge>
      );
    }

    // Normal redirects (info)
    if (site.redirect_count > 0 && site.redirect_count <= 5) {
      return (
        <Badge variant="default" className="text-xs" title={`Final: ${site.final_url || 'checking...'}`}>
          {site.redirect_count} Redirect{site.redirect_count > 1 ? 's' : ''}
        </Badge>
      );
    }

    // No redirects
    return null;
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-emerald-500/20 border-t-emerald-500"></div>
        <span className="ml-3 text-gray-400 text-sm">Loading sites...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center py-16">
        <div className="bg-red-500/5 border border-red-500/10 rounded-2xl p-6 max-w-md">
          <div className="flex items-center gap-3 mb-2">
            <svg className="w-6 h-6 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <h3 className="text-lg font-semibold text-white">Failed to Load Data</h3>
          </div>
          <p className="text-red-300/80 mb-3 text-sm">Unable to load your monitored sites. Redirecting to login...</p>
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-5 w-5 border-2 border-red-500/20 border-t-red-500"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div className="flex items-center gap-3">
          <div className="relative flex items-center justify-center w-10 h-10">
            <div className="absolute inset-0 bg-emerald-500/20 rounded-xl blur-md" />
            <div className="relative bg-emerald-500/10 border border-emerald-500/20 rounded-xl w-10 h-10 flex items-center justify-center">
              <svg className="w-5 h-5 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
          </div>
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-white tracking-tight">Monitored Sites</h2>
            <p className="text-sm text-gray-500">
              {sites.length} site{sites.length !== 1 ? 's' : ''}
              {sites.length > 0 && (
                <span className="ml-1">
                  &middot; <span className="text-emerald-400/80">{sites.filter(s => s.status === 'UP').length} online</span>
                  {sites.filter(s => s.status === 'DOWN').length > 0 && (
                    <span className="text-red-400/80 ml-1">&middot; {sites.filter(s => s.status === 'DOWN').length} down</span>
                  )}
                </span>
              )}
            </p>
          </div>
        </div>
        <button
          onClick={loadSites}
          className="flex items-center justify-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-sm text-gray-300 hover:bg-white/10 hover:text-white hover:border-white/20 transition-all w-full sm:w-auto"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Refresh
        </button>
      </div>

      {sites.length === 0 ? (
        <div className="text-center py-12 sm:py-16 bg-[#0b1120] border border-white/5 rounded-2xl">
          <div className="text-4xl sm:text-6xl mb-3 sm:mb-4">🌐</div>
          <h3 className="text-base sm:text-lg font-medium text-white mb-2">No sites being monitored yet</h3>
          <p className="text-sm text-gray-500 px-4">Add your first site above to start monitoring!</p>
        </div>
      ) : (
        <div className="grid gap-4 sm:gap-5">
          {sites.map(site => {
            let borderColor, glowColor;
            if (site.status === 'DOWN') {
              borderColor = 'border-l-red-500';
              glowColor = 'shadow-red-500/10';
            } else if (site.status === 'UP') {
              borderColor = 'border-l-emerald-500';
              glowColor = 'shadow-emerald-500/10';
            } else {
              borderColor = 'border-l-gray-500';
              glowColor = '';
            }
            
            return (
              <div 
                key={site.id} 
                className={`relative bg-[#0b1120] rounded-2xl border border-white/5 border-l-[5px] ${borderColor} p-5 sm:p-6 transition-all duration-300 hover:-translate-y-0.5 hover:border-white/10 ${glowColor} hover:shadow-lg group/card`}
              >
                {/* Header Section */}
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap mb-2.5">
                      <h3 className="text-lg sm:text-xl font-bold text-white truncate hover:text-emerald-400 transition-colors">
                        {/^https?:\/\//i.test(site.url) ? (
                          <a href={site.url} target="_blank" rel="noopener noreferrer">
                            {site.url}
                          </a>
                        ) : (
                          site.url
                        )}
                      </h3>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                        site.status === 'UP' ? 'bg-emerald-500/15 text-emerald-400 ring-1 ring-emerald-500/20' :
                        site.status === 'DOWN' ? 'bg-red-500/15 text-red-400 ring-1 ring-red-500/20' :
                        'bg-white/5 text-gray-400 ring-1 ring-white/10'
                      }`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${
                          site.status === 'UP' ? 'bg-emerald-400' :
                          site.status === 'DOWN' ? 'bg-red-400 animate-pulse' :
                          'bg-gray-400'
                        }`} />
                        {site.status}
                      </span>
                      
                      {/* SSL Status */}
                      {site.ssl_check_enabled && renderSSLStatus(site)}
                      
                      {/* Redirect Status */}
                      {site.redirect_enabled && renderRedirectStatus(site)}
                    </div>
                    
                    {/* Metrics Row */}
                    <div className="flex items-center gap-2 text-sm flex-wrap">
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white/5 rounded-full text-xs text-gray-400">
                        <svg className="w-3.5 h-3.5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {formatLastChecked(site.last_checked)}
                      </span>
                      
                      {site.last_code && (
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                          site.last_code >= 200 && site.last_code < 300 
                            ? 'bg-emerald-500/10 text-emerald-400'
                            : 'bg-white/5 text-gray-400'
                        }`}>
                          HTTP {site.last_code}
                        </span>
                      )}
                      
                      {site.response_time != null && (
                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${
                          site.response_time < 500 
                            ? 'bg-emerald-500/10 text-emerald-400'
                            : site.response_time < 1000
                            ? 'bg-yellow-500/10 text-yellow-400'
                            : 'bg-red-500/10 text-red-400'
                        }`}>
                          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
                          </svg>
                          {formatResponseTime(site.response_time)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-4 border-t border-white/5">
                  <div className="flex items-center gap-2 w-full sm:w-auto">
                    <button
                      onClick={() => toggleLogs(site.id)}
                      className={`flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs transition-all ${
                        open === site.id
                          ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                          : 'bg-white/5 text-gray-400 border border-white/5 hover:bg-white/10 hover:text-white hover:border-white/10'
                      }`}
                    >
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      {open === site.id ? "Hide Logs" : "Logs"}
                    </button>
                    <button
                      onClick={() => togglePerformance(site.id)}
                      className={`flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs transition-all ${
                        performanceOpen === site.id
                          ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                          : 'bg-white/5 text-gray-400 border border-white/5 hover:bg-white/10 hover:text-white hover:border-white/10'
                      }`}
                    >
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                      Analytics
                    </button>
                    <button
                      onClick={() => handleCheckNow(site.id)}
                      disabled={busyId === site.id}
                      className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-3 py-1.5 bg-emerald-500/15 text-emerald-400 border border-emerald-500/20 rounded-lg text-xs font-medium hover:bg-emerald-500/25 transition-all disabled:opacity-40"
                    >
                      {busyId === site.id ? (
                        <>
                          <div className="w-3 h-3 border-2 border-emerald-400/30 border-t-emerald-400 rounded-full animate-spin" />
                          Checking...
                        </>
                      ) : (
                        <>
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                          Check Now
                        </>
                      )}
                    </button>
                  </div>

                  <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
                    <span className="text-[11px] text-gray-600 hidden sm:inline-block">
                      checks ~every 2m
                    </span>
                    {deleteConfirm === site.id ? (
                      <div className="flex gap-2 animate-in fade-in zoom-in duration-200">
                        <button
                          onClick={() => handleDeleteConfirm(site.id)}
                          className="px-3 py-1.5 bg-red-500/15 text-red-400 border border-red-500/20 rounded-lg text-xs font-medium hover:bg-red-500/25 transition-all"
                        >
                          Confirm Delete
                        </button>
                        <button
                          onClick={handleDeleteCancel}
                          className="px-3 py-1.5 bg-white/5 text-gray-400 border border-white/5 rounded-lg text-xs hover:bg-white/10 transition-all"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => handleDeleteClick(site.id)}
                        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs text-gray-600 hover:text-red-400 hover:bg-red-500/10 border border-transparent hover:border-red-500/20 transition-all opacity-0 group-hover/card:opacity-100 focus:opacity-100"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        Delete
                      </button>
                    )}
                  </div>
                </div>

                {open === site.id && (
                  <div className="mt-5 pt-5 border-t border-white/5 animate-in fade-in slide-in-from-top-2 duration-200">
                    <h4 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
                      <svg className="w-4 h-4 text-emerald-400/60" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Recent Activity
                    </h4>
                    {/* Quick stats */}
                    <div className="mb-4 flex flex-wrap gap-2 text-xs">
                      {(() => {
                        const s = computeLogStats(logs);
                        return (
                          <>
                            <span className="px-2.5 py-1 rounded-full bg-white/5 text-gray-400">Samples: {s.count}</span>
                            {s.uptimePct != null && (
                              <span className="px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-400">Uptime {s.uptimePct}%</span>
                            )}
                            {s.avgMs != null && (
                              <span className="px-2.5 py-1 rounded-full bg-cyan-500/10 text-cyan-400">Avg {formatResponseTime(s.avgMs)}</span>
                            )}
                            {s.p95Ms != null && (
                              <span className="px-2.5 py-1 rounded-full bg-purple-500/10 text-purple-400">P95 {formatResponseTime(s.p95Ms)}</span>
                            )}
                            {s.lastCode != null && (
                              <span className="px-2.5 py-1 rounded-full bg-white/5 text-gray-400">Last HTTP {s.lastCode}</span>
                            )}
                          </>
                        );
                      })()}
                    </div>
                    <div className="overflow-x-auto rounded-xl bg-white/[0.02] border border-white/5">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left border-b border-white/5">
                            <th className="px-4 py-2.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Time</th>
                            <th className="px-4 py-2.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                            <th className="px-4 py-2.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wider">HTTP</th>
                            <th className="px-4 py-2.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Response</th>
                            {site.ssl_check_enabled && <th className="px-4 py-2.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wider">SSL</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {logs.map((log, i) => (
                            <tr key={i} className="border-b border-white/[0.03] last:border-0 hover:bg-white/[0.02] transition-colors">
                              <td className="px-4 py-2.5 text-gray-400 text-xs">
                                {new Date(log.checked_at).toLocaleString()}
                              </td>
                              <td className="px-4 py-2.5">
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                                  log.status === 'UP' ? 'bg-emerald-500/10 text-emerald-400' :
                                  log.status === 'DOWN' ? 'bg-red-500/10 text-red-400' :
                                  'bg-white/5 text-gray-400'
                                }`}>
                                  <span className={`w-1 h-1 rounded-full ${
                                    log.status === 'UP' ? 'bg-emerald-400' : log.status === 'DOWN' ? 'bg-red-400' : 'bg-gray-400'
                                  }`} />
                                  {log.status}
                                </span>
                              </td>
                              <td className="px-4 py-2.5 text-gray-400 text-xs">
                                {log.code ?? "—"}
                              </td>
                              <td className="px-4 py-2.5 text-gray-400 text-xs">
                                {formatResponseTime(log.ms)}
                              </td>
                              {site.ssl_check_enabled && (
                                <td className="px-4 py-2.5">
                                  {log.ssl_status ? (
                                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                                      log.ssl_status === 'valid' ? 'bg-emerald-500/10 text-emerald-400' :
                                      log.ssl_status === 'expiring' ? 'bg-yellow-500/10 text-yellow-400' :
                                      'bg-red-500/10 text-red-400'
                                    }`}>
                                      {log.ssl_status}
                                    </span>
                                  ) : <span className="text-gray-600 text-xs">—</span>}
                                </td>
                              )}
                            </tr>
                          ))}
                          {logs.length === 0 && (
                            <tr>
                              <td colSpan={site.ssl_check_enabled ? "5" : "4"} className="py-6 text-center text-gray-600 text-sm">
                                No activity logs yet.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {performanceOpen === site.id && (
                  <div className="mt-5 pt-5 border-t border-white/5">
                    <SiteAnalytics siteId={site.id} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
